import { operationIds } from "@gen/app-access-config";
import { Query } from "@gen/app-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useParams } from "@tanstack/react-router";

import { LoadingScreen } from "~/components/loading_screen";
import { SectionSideNav } from "~/components/section_side_nav";
import { useCanExecuteOperation } from "~/contexts/auth_query";

import { AccountBalance } from "./AccountBalance";
import { OutstandingRefundCosts } from "./OutstandingRefundCosts";
import { PayoutHistory } from "./PayoutHistory";
import { PayoutsPausedBanner } from "./PayoutsPausedBanner";

export function Account() {
  const canExecuteOperation = useCanExecuteOperation();

  const clientId = useParams({
    from: "/clients/$clientId/account",
    select(params) {
      return params.clientId;
    },
  });

  const { data: clientData, isFetching: isClientFetching } =
    Query.useCore_getClientQuery(clientId);

  const { data: clientFlagsData, isFetching: isClientFlagsFetching } =
    Query.useCore_getClientFlagsQuery(clientId);

  const canShowOutstandingRefundCosts = canExecuteOperation(
    operationIds["core.getClientPendingTransferReversals"],
  );
  const { data: pendingTransferReversalsData } =
    Query.useCore_getClientPendingTransferReversalsQuery(clientId, {
      enabled: canShowOutstandingRefundCosts,
    });
  const hasOutstandingRefundCosts =
    canShowOutstandingRefundCosts &&
    (pendingTransferReversalsData?.length ?? 0) > 0;
  const hasOutstandingRefundCostAmount =
    canShowOutstandingRefundCosts &&
    (pendingTransferReversalsData ?? []).some(
      (reversal) => reversal.amount > 0,
    );

  const hasConnectedAccount = Boolean(
    clientData?.on_behalf_of_stripe_connected_account_id,
  );
  const publishableKey = clientData?.stripe_publishable_key;
  const canShowAccountBalance =
    canExecuteOperation(operationIds["core.getClientAccountSession"]) &&
    clientFlagsData?.show_account_balance &&
    hasConnectedAccount &&
    publishableKey;

  if (isClientFetching || isClientFlagsFetching) {
    return <LoadingScreen />;
  }

  if (!canShowAccountBalance) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
        <Typography variant="body1">
          Account details are not available for this client.
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: { xs: "block", md: "flex" },
        gap: 4.5,
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: { xs: "contents", md: "block" },
          order: { md: 1 },
          flex: { md: "0 0 200px" },
          minWidth: 0,
          alignSelf: "stretch",
        }}
      >
        <SectionSideNav
          items={[
            { id: "account_balance", label: "Account Balance" },
            ...(hasOutstandingRefundCosts
              ? [
                  {
                    id: "outstanding_refund_costs",
                    label: "Outstanding Refund Costs",
                  },
                ]
              : []),
            { id: "payout_history", label: "Payout History" },
          ]}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={{ xs: 5 }}>
          {hasOutstandingRefundCostAmount && <PayoutsPausedBanner />}
          <AccountBalance
            clientId={clientId}
            publishableKey={publishableKey}
            isAdmin={false}
            isPayoutsPaused={hasOutstandingRefundCosts}
          />
          {hasOutstandingRefundCosts && (
            <OutstandingRefundCosts
              clientId={clientId}
              debtThreshold={clientData?.debt_threshold}
            />
          )}
          <PayoutHistory clientId={clientId} />
        </Stack>
      </Box>
    </Box>
  );
}
